import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCompressAlt,
  faTrashAlt,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import DropdownButton from 'react-bootstrap/DropdownButton';
import getItemIdsByRank from './getItemsByRank';
import './HierarchyDisplay.css';
import { ItemType, Relationship } from '../types';
import Button from 'react-bootstrap/Button';

interface ItemProps {
  addItemForCompare?: () => void;
  children: React.ReactNode;
  deleteItem?: () => void;
  deleteRelationships?: () => void;
  onClick?: () => void;
}

const Item = ({
  addItemForCompare,
  children,
  deleteItem,
  deleteRelationships,
  onClick,
}: ItemProps) => {
  const [mouseOver, setMouseOver] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const handleDelete = (action: () => void) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      action();
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (showDropdown) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  const handleToggle = (isOpen: boolean, event: any) => {
    if (event?.nativeEvent?.type === 'contextmenu') {
      setShowDropdown(!showDropdown);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (addItemForCompare) {
      addItemForCompare();
    }
    if (onClick) {
      onClick();
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  return (
    <div
      className="hierarchy-item"
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
      role="button"
      tabIndex={0}
    >
      <DropdownButton
        className="dropdown-toggle-no-arrow"
        show={showDropdown}
        onToggle={handleToggle}
        title={children}
        variant={mouseOver ? 'primary' : 'light'}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        {addItemForCompare && (
          <Dropdown.Item onClick={addItemForCompare} className="text-primary">
            <FontAwesomeIcon icon={faCompressAlt} className="fa-fw me-2" />
            Compare
          </Dropdown.Item>
        )}
        {(deleteItem || deleteRelationships) && <Dropdown.Divider />}
        {deleteRelationships && (
          <Dropdown.Item
            onClick={() => handleDelete(deleteRelationships)}
            className="text-warning"
          >
            <FontAwesomeIcon icon={faTrashAlt} className="fa-fw me-2" />
            Delete Relationships
          </Dropdown.Item>
        )}
        {deleteItem && (
          <Dropdown.Item
            onClick={() => handleDelete(deleteItem)}
            className="text-danger"
          >
            <FontAwesomeIcon icon={faTrashAlt} className="fa-fw me-2" />
            Delete Item &amp; Relationships
          </Dropdown.Item>
        )}
      </DropdownButton>
    </div>
  );
};

interface HierarchyDisplayProps {
  addItemForCompare: (itemId: string) => void;
  deleteItem: (itemId: string) => void;
  deleteRelationships: (itemId: string) => void;
  items: { [itemId: string]: ItemType };
  relationships: { [relationshipId: string]: Relationship };
  onItemClick?: (itemId: string) => void;
}

const HierarchyDisplay = ({
  addItemForCompare,
  deleteItem,
  deleteRelationships,
  items,
  relationships,
  onItemClick,
}: HierarchyDisplayProps) => {
  const { itemIds, errorMessage, itemsInCycles, cycleGroups } =
    getItemIdsByRank(relationships, items);

  const handleResetCycle = async (cycleGroup: string[]) => {
    // Find all relationships between items in this cycle group
    const relationshipsToDelete = Object.values(relationships).filter(
      (rel) =>
        cycleGroup.includes(rel.options[0]) &&
        cycleGroup.includes(rel.options[1])
    );

    // Delete each relationship and wait for all deletions to complete
    await Promise.all(
      relationshipsToDelete.map((rel) => deleteRelationships(rel.options[0]))
    );
  };

  return (
    <div>
      {errorMessage && (
        <div className="p-3">
          <div className="text-danger mb-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {errorMessage}
          </div>
        </div>
      )}
      {Object.entries(itemIds)
        .reverse()
        .map(([rank, _items], i) => (
          <div key={rank} className="hierarchy-rank">
            <span className="rank-number">{i + 1}.</span>
            {_items.map((itemId) => (
              <Item
                key={itemId}
                addItemForCompare={() => addItemForCompare(itemId)}
                deleteItem={() => deleteItem(itemId)}
                deleteRelationships={() => deleteRelationships(itemId)}
                onClick={() => onItemClick?.(itemId)}
              >
                {itemId}
              </Item>
            ))}
          </div>
        ))}
      {cycleGroups && cycleGroups.length > 0 && (
        <div className="mt-4">
          <h6 className="text-warning mb-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            Items with Contradictory Relationships
          </h6>
          {cycleGroups.map((cycleGroup, index) => (
            <div key={index} className="cycle-group mb-3">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  {cycleGroup.map((itemId) => (
                    <Item
                      key={itemId}
                      addItemForCompare={() => addItemForCompare(itemId)}
                      deleteItem={() => deleteItem(itemId)}
                      deleteRelationships={() => deleteRelationships(itemId)}
                      onClick={() => onItemClick?.(itemId)}
                    >
                      {itemId}
                    </Item>
                  ))}
                </div>
                <Button
                  variant="outline-warning"
                  size="sm"
                  className="ms-3"
                  onClick={() => handleResetCycle(cycleGroup)}
                >
                  Reset Cycle
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HierarchyDisplay;
